import { memo } from "react";
import { SmoothLink as Link } from "@/components/navigation";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, CheckCircle } from "lucide-react";
import type { Treatment } from "@/types/schema";

interface QuickInfoSidebarProps {
  treatment: Treatment;
  doctorCount: number;
  hospitalCount: number;
}

const includedItems = [
  "Hospital accommodation",
  "Surgeon & anesthesia fees",
  "Post-operative care",
  "Airport transfers",
  "24/7 patient coordinator",
  "Follow-up consultations",
];

export const QuickInfoSidebar = memo(function QuickInfoSidebar({ 
  treatment, 
  doctorCount, 
  hospitalCount 
}: QuickInfoSidebarProps) {
  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Quick Info */}
      <Card>
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle as="h2" className="text-base sm:text-lg">Quick Info</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 sm:space-y-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Starting Price</span>
            <span className="font-semibold text-primary">
              ${treatment.price_india_min?.toLocaleString()}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Duration</span>
            <span className="font-semibold">
              {treatment.duration_days} days
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Savings</span>
            <Badge className="bg-accent/10 text-accent text-xs">
              Up to {treatment.savings_percentage}%
            </Badge>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Doctors</span>
            <span className="font-semibold">
              {doctorCount > 0 ? `${doctorCount} available` : "Specialists On Demand"}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Hospitals</span>
            <span className="font-semibold">
              {hospitalCount > 0 ? `${hospitalCount} available` : "Wide Network"}
            </span>
          </div>

          <div className="pt-3 sm:pt-4 border-t space-y-2 sm:space-y-3">
            <Link to="/contact/">
              <Button className="w-full rounded-full text-sm">
                Get Free Consultation
              </Button>
            </Link>
            <Button variant="outline" className="w-full rounded-full text-sm">
              <Calendar className="w-4 h-4 mr-2" />
              Schedule Video Call
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* What's Included */}
      <Card>
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle as="h2" className="text-base sm:text-lg">What's Included</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 sm:space-y-3">
            {includedItems.map((item) => (
              <li key={item} className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-accent flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
});
